import {
  getCookie,
  getStoredUtmParams,
} from 'public/assets/js/analytics/cookie';

const PAID_UTM_MEDIUM = 'paid';

/**
 * Ad-click identifiers Google and Microsoft append to their landing URLs.
 * `cookie.js` persists both for 60 days, so they still identify paid traffic
 * after a redirect or an internal navigation has dropped the UTM params.
 */
const PAID_CLICK_IDS = ['gclid', 'msclkid'];

const getCountryFromOneTrustApi = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const country = window.OneTrust?.getGeolocationData?.()?.country;
  return country ? country.toUpperCase() : null;
};

/**
 * OneTrust writes `geolocation=US;CA` (country;region) onto OptanonConsent.
 * This is available before the OneTrust JS API is, including on later page
 * views in the same session.
 */
const getCountryFromOptanonConsent = (): string | null => {
  const raw = getCookie('OptanonConsent');
  if (!raw) {
    return null;
  }

  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    decoded = raw;
  }

  const match = /(?:^|&)geolocation=([A-Za-z]{2})/.exec(decoded);
  return match?.[1] ? match[1].toUpperCase() : null;
};

/**
 * Visitor country from OneTrust (API, then OptanonConsent cookie).
 *
 * Do not use the `locale` cookie: that is Accept-Language / site locale, not
 * IP geo. CloudFront viewer-country is only on the request at Lambda@Edge.
 */
export const getVisitorCountry = (): string | null => {
  return getCountryFromOneTrustApi() ?? getCountryFromOptanonConsent();
};

const isPaidMedium = (params: Record<string, unknown>): boolean => {
  const medium = String(
    params.utm_medium__c ?? params.utm_medium ?? '',
  ).toLowerCase();

  return medium === PAID_UTM_MEDIUM;
};

const getUrlParams = (): URLSearchParams | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  return new URLSearchParams(window.location.search);
};

const isPaidMediumFromUrl = (): boolean => {
  const params = getUrlParams();
  if (!params) {
    return false;
  }

  return isPaidMedium({ utm_medium: params.get('utm_medium') });
};

const hasStoredUtm = (params: Record<string, unknown>): boolean => {
  return Boolean(
    params.utm_medium__c ||
      params.utm_medium ||
      params.utm_source__c ||
      params.utm_source,
  );
};

/**
 * Last-touch `utm_medium=paid`, across every source rather than only search.
 * Trust the stored UTM cookie when it has attribution; fall back to the current
 * URL only before that cookie exists (the landing hit, before setUtm writes it).
 */
const isPaidUtmMedium = (): boolean => {
  const stored = getStoredUtmParams();

  if (hasStoredUtm(stored)) {
    return isPaidMedium(stored);
  }

  return isPaidMediumFromUrl();
};

/**
 * Catches paid clicks that `utm_medium` misses: ads that landed without UTM
 * params, and sessions whose UTM cookie was never written because the landing
 * URL carried only one of `utm_source` / `utm_medium`.
 */
const hasPaidClickId = (): boolean => {
  if (
    PAID_CLICK_IDS.some((name) => {
      return Boolean(getCookie(name));
    })
  ) {
    return true;
  }

  const params = getUrlParams();
  if (!params) {
    return false;
  }

  return PAID_CLICK_IDS.some((name) => {
    return Boolean(params.get(name));
  });
};

/**
 * Any paid acquisition, checked cheapest signal first: the `utm_medium` the
 * team's reporting agrees on, then the ad-click cookies that survive it.
 */
export const isPaidTraffic = (): boolean => {
  return isPaidUtmMedium() || hasPaidClickId();
};

/**
 * True when the visitor is in the US, whether organic or paid. Unknown country
 * is treated as ineligible so we do not require Phone outside the experiment
 * audience.
 */
export const isUsOrganicFormTraffic = (): boolean => {
  return getVisitorCountry() === 'US';
};
